import React, { useMemo } from 'react';
import styled from 'styled-components';
import { IgnoredChecksBreakdown, SplitChecksToggleBreakdown, ToggleableBreakdown } from 'components';
import { theme, toRem } from 'design';
import { ICalculatorRule, ICategoryGroup, IPOSSegment, ITipCalculatorResultEmployee } from 'types';
import { buildEmployeeOverrideTicketBreakdown, sortAlphaNumerically } from 'utils';

export interface ICalculationBreakdownListProps {
  calculations: ITipCalculatorResultEmployee[];
  calculatorRules: ICalculatorRule[];
  categoryGroups: ICategoryGroup[];
  segments?: IPOSSegment[];
  creditCardFee?: number;
  contributingOnly?: boolean;
  className?: string;
}

const List = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.lg)};
`;

const EmptyState = styled.p`
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(theme.font.size.md)};
  text-align: center;
`;

const CalculationBreakdownList = ({ calculations, calculatorRules, categoryGroups, segments, creditCardFee = 0, contributingOnly = false, className }: ICalculationBreakdownListProps) => {
  const { splitChecks, ignoredChecks } = useMemo(() => buildEmployeeOverrideTicketBreakdown(calculations), [calculations]);
  const hasSplitChecks = !!splitChecks.received?.length || !!splitChecks.contributions?.length;
  const hasIgnoredChecks = !!ignoredChecks.length;

  const calculationItems = useMemo(
    () =>
      calculations.reduce<React.ReactNode[]>((acc, calculation, idx) => {
        const hasActivity = calculation.received.length || calculation.contributions?.length || calculation.retained?.total > 0;
        if (contributingOnly || hasActivity) {
          acc.push(
            <ToggleableBreakdown
              key={`${calculation.id}_${calculation.segmentId}_${idx}`}
              calculatorRules={calculatorRules}
              categoryGroups={categoryGroups}
              calculation={calculation}
              creditCardFee={creditCardFee}
              segmentName={segments?.find((segment) => segment.id === calculation.segmentId)?.name}
              contributingOnly={contributingOnly}
            />
          );
        }
        return acc;
      }, []),
    [calculations, segments, calculatorRules, categoryGroups, creditCardFee, contributingOnly]
  );

  const isEmpty = !calculationItems.length && !hasSplitChecks && !hasIgnoredChecks;

  return (
    <List className={className}>
      {isEmpty ? (
        <EmptyState>No details to display.</EmptyState>
      ) : (
        <>
          {calculationItems}
          {hasSplitChecks && <SplitChecksToggleBreakdown splitChecks={splitChecks} contributingOnly={contributingOnly} />}
          {hasIgnoredChecks && <IgnoredChecksBreakdown ignoredCheckData={ignoredChecks.sort(sortAlphaNumerically('displayId'))} />}
        </>
      )}
    </List>
  );
};

export default CalculationBreakdownList;
